/**
 * Grouping a flat image list back into clips and series.
 *
 * Extracted from `StudyImagesModal` so the assessment viewer and the
 * clinical viewer group frames identically — a cine clip should look
 * like a cine clip in both, and two copies of this logic would drift.
 *
 * Pure functions on purpose: no React, no fetching, so both callers can
 * use them and both are covered by one set of tests.
 */

/** The minimum an image needs for grouping. Both viewers' image types
 *  are structurally compatible with this. */
export interface StackImage {
  sop_instance_uid: string;
  frame_number: number | null;
}

export interface ImageGroup<T extends StackImage = StackImage> {
  key: string;
  images: T[];
  /** True when this group is a multi-frame clip rather than a still. */
  isClip: boolean;
  /** The frame to show as the group's thumbnail. */
  rep: T;
}

/**
 * The clip an image belongs to, or null for a still.
 *
 * Cine frames carry a synthetic `{sop}~f{index}` uid plus a non-null
 * `frame_number`; the frame number is what distinguishes them, because a
 * still's uid could in principle contain `~f` too.
 */
export function clipKeyOf(img: StackImage): string | null {
  if (img.frame_number == null) return null;
  const i = img.sop_instance_uid.lastIndexOf("~f");
  return i >= 0 ? img.sop_instance_uid.slice(0, i) : img.sop_instance_uid;
}

/**
 * Collapse each clip's contiguous frames into one group; a still becomes
 * its own singleton.
 *
 * Relies on the list arriving in order, so a clip's frames are already
 * adjacent. That is guaranteed by the ordering in the listing endpoints
 * (series, then instance, then frame).
 */
export function buildGroups<T extends StackImage>(images: T[]): ImageGroup<T>[] {
  const out: ImageGroup<T>[] = [];
  for (const img of images) {
    const ck = clipKeyOf(img);
    const last = out[out.length - 1];
    if (ck != null && last?.isClip && last.key === ck) {
      last.images.push(img);
    } else if (ck != null) {
      out.push({ key: ck, images: [img], isClip: true, rep: img });
    } else {
      out.push({
        key: img.sop_instance_uid,
        images: [img],
        isClip: false,
        rep: img,
      });
    }
  }
  // A clip's middle frame is a better thumbnail than its first, which on
  // an ultrasound sweep is often the probe still settling.
  for (const g of out) {
    if (g.isClip) {
      const mid = g.images[Math.floor(g.images.length / 2)];
      if (mid) g.rep = mid;
    }
  }
  return out;
}

/** An image carrying enough to be grouped into a series. */
export interface SeriesImage extends StackImage {
  series_instance_uid: string | null;
  series_number: number | null;
  series_description: string | null;
}

export interface Series<T extends SeriesImage = SeriesImage> {
  key: string;
  label: string;
  images: T[];
}

/**
 * Split a case's images into series.
 *
 * CT and MR studies are multi-series and a candidate needs to move
 * between them — the clinical viewer has no concept of this at all,
 * because ultrasound does not need one.
 *
 * Grouped by `series_instance_uid` rather than `series_number`, since
 * the number is optional and not guaranteed unique across a study.
 * Everything without a series lands in one group, which is the ordinary
 * shape of an ultrasound case.
 */
export function buildSeries<T extends SeriesImage>(images: T[]): Series<T>[] {
  const byKey = new Map<string, T[]>();
  for (const img of images) {
    const key = img.series_instance_uid ?? "__none__";
    const bucket = byKey.get(key);
    if (bucket) bucket.push(img);
    else byKey.set(key, [img]);
  }

  return [...byKey.entries()].map(([key, imgs]) => {
    const first = imgs[0];
    const described = imgs.find((i) => i.series_description)?.series_description;
    const number = imgs.find((i) => i.series_number != null)?.series_number;
    const label =
      described ??
      (number != null ? `Series ${number}` : "Images");
    return { key: key === "__none__" ? "series-none" : key, label, images: imgs, first };
  }).map(({ key, label, images: imgs }) => ({ key, label, images: imgs }));
}

// ---------------------------------------------------------------------
// Windows
//
// One instance can be stored several times, rendered at a different
// window each: soft tissue, lung, bone. They arrive interleaved in the
// flat list, adjacent to each other because the listing orders by
// window within an instance.
//
// The viewer must NOT treat them as extra slices. A 355-slice CT stored
// at three windows is 1065 rows, and scrolling through all of them would
// show every slice three times.
// ---------------------------------------------------------------------

/** An image that may be one of several renderings of its instance. */
export interface WindowedImage extends StackImage {
  window_label?: string;
}

/** What every image without an explicit label is. */
export const DEFAULT_WINDOW = "default";

export function windowOf(img: WindowedImage): string {
  return img.window_label || DEFAULT_WINDOW;
}

/**
 * The windows this case offers, in the order the server listed them.
 *
 * A single entry means there is nothing to choose between and the viewer
 * shows no selector — which is every case ingested before multi-window
 * existed, and every ultrasound, MR and plain film regardless.
 */
export function windowsOf(images: WindowedImage[]): string[] {
  const out: string[] = [];
  for (const img of images) {
    const label = windowOf(img);
    if (!out.includes(label)) out.push(label);
  }
  return out;
}

/**
 * Just the images rendered at one window.
 *
 * ORDER IS PRESERVED, and that is the property the viewer depends on:
 * every window holds the same instances in the same sequence, so slice
 * N of the bone window is the same anatomy as slice N of the soft
 * window. Switching window can therefore keep the frame index and the
 * candidate stays exactly where they were looking — which is what makes
 * it a window control rather than a navigation jump.
 *
 * Falls back to the whole list if the label is not present, so a stale
 * selection can never blank the viewer.
 */
export function atWindow<T extends WindowedImage>(
  images: T[],
  label: string,
): T[] {
  const picked = images.filter((img) => windowOf(img) === label);
  return picked.length > 0 ? picked : images;
}
